import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useAssessmentPlanFilterOptions } from "../../../hooks/useAssessmentPlanFilterOptions";
import AiPlanSelectClearButton from "../AiPlanSelectClearButton";

const formFieldLabelSx = {
  fontSize: "0.875rem",
  fontWeight: 500,
  color: "#262626",
  mb: 0.75,
};

const selectFormControlSx = {
  position: "relative" as const,
  width: "100%",
};

const PLACEHOLDER = {
  objectType: "Select...",
  objectSubType: "Select...",
} as const;

const REQUIRED_FIELD_MESSAGE = "This field is required.";

function RequiredFieldHelperText({ message }: Readonly<{ message: string }>) {
  return (
    <FormHelperText
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        mx: 0,
        mt: 0.75,
      }}
    >
      <ErrorOutlineIcon sx={{ fontSize: 14 }} />
      {message}
    </FormHelperText>
  );
}
function renderSelectPlaceholder(placeholder: string) {
  return (
    <Box component="span" sx={{ color: "#9ca3af" }}>
      {placeholder}
    </Box>
  );
}

function namedOptionLabel(
  selected: string,
  options: Array<{ code: string; name: string }>,
  placeholder: string,
) {
  if (!selected) return renderSelectPlaceholder(placeholder);
  return options.find((option) => option.code === selected)?.name ?? selected;
}

export type EnhancedTaggingRuleObjectSelection = {
  objectType: string;
  objectSubType: string;
};

type EnhancedTaggingRuleObjectStepProps = Readonly<{
  value: EnhancedTaggingRuleObjectSelection;
  onChange: (next: EnhancedTaggingRuleObjectSelection) => void;
  showValidation?: boolean;
}>;

export default function EnhancedTaggingRuleObjectStep({
  value,
  onChange,
  showValidation = false,
}: EnhancedTaggingRuleObjectStepProps) {
  const {
    objectTypeOptions,
    getObjectSubTypeOptions,
    planTypesLoading,
  } = useAssessmentPlanFilterOptions();

  const objectSubTypeOptions = value.objectType
    ? getObjectSubTypeOptions(value.objectType)
    : [];
  const requiresObjectSubType = objectSubTypeOptions.length > 0;
  // Always show Sub-Type by default; hide only when a type has no sub-types (e.g. RTQ).
  const showObjectSubType = !value.objectType || requiresObjectSubType;
  const subTypeDisabled = planTypesLoading || !value.objectType;

  const objectTypeError =
    showValidation && !value.objectType.trim() ? REQUIRED_FIELD_MESSAGE : "";
  const objectSubTypeError =
    showValidation && showObjectSubType && !value.objectSubType.trim()
      ? REQUIRED_FIELD_MESSAGE
      : "";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, width: "100%" }}>
      <Box>
        <Typography sx={formFieldLabelSx}>
          Object Type
          <Box component="span" sx={{ color: "error.main", ml: 0.25 }}>
            *
          </Box>
        </Typography>
        <FormControl
          size="small"
          error={Boolean(objectTypeError)}
          sx={selectFormControlSx}
        >
          <Select
            displayEmpty
            value={value.objectType}
            disabled={planTypesLoading}
            onChange={(event) => {
              // Maps to API `target_type`; first sub-type preselected as `sub_type` when present.
              const nextObjectType = event.target.value;
              const nextSubTypes = getObjectSubTypeOptions(nextObjectType);
              onChange({
                objectType: nextObjectType,
                objectSubType: nextSubTypes[0]?.code ?? "",
              });
            }}
            renderValue={(selected) =>
              namedOptionLabel(
                String(selected ?? ""),
                objectTypeOptions,
                PLACEHOLDER.objectType,
              )
            }
            MenuProps={{
              PaperProps: { sx: { maxHeight: 300 } },
            }}
          >
            {objectTypeOptions.map((option) => (
              <MenuItem key={option.code} value={option.code}>
                {option.name}
              </MenuItem>
            ))}
          </Select>
          <AiPlanSelectClearButton
            visible={Boolean(value.objectType)}
            disabled={planTypesLoading}
            aria-label="Clear Object Type"
            onClear={() => onChange({ objectType: "", objectSubType: "" })}
          />
          {objectTypeError ? (
            <RequiredFieldHelperText message={objectTypeError} />
          ) : null}
        </FormControl>
      </Box>

      {showObjectSubType ? (
        <Box>
          <Typography sx={formFieldLabelSx}>
            Object Sub-Type
            <Box component="span" sx={{ color: "error.main", ml: 0.25 }}>
              *
            </Box>
          </Typography>
          <FormControl
            size="small"
            error={Boolean(objectSubTypeError)}
            sx={selectFormControlSx}
          >
            <Select
              displayEmpty
              value={value.objectType ? value.objectSubType : ""}
              disabled={subTypeDisabled}
              onChange={(event) =>
                onChange({
                  ...value,
                  objectSubType: event.target.value,
                })
              }
              renderValue={(selected) =>
                namedOptionLabel(
                  String(selected ?? ""),
                  objectSubTypeOptions,
                  PLACEHOLDER.objectSubType,
                )
              }
              MenuProps={{
                PaperProps: { sx: { maxHeight: 300 } },
              }}
            >
              {objectSubTypeOptions.map((option) => (
                <MenuItem key={option.code} value={option.code}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
            <AiPlanSelectClearButton
              visible={Boolean(value.objectType && value.objectSubType)}
              disabled={subTypeDisabled}
              aria-label="Clear Object Sub-Type"
              onClear={() => onChange({ ...value, objectSubType: "" })}
            />
            {objectSubTypeError ? (
              <RequiredFieldHelperText message={objectSubTypeError} />
            ) : null}
          </FormControl>
        </Box>
      ) : null}
    </Box>
  );
}

export function createEmptyObjectSelection(): EnhancedTaggingRuleObjectSelection {
  return { objectType: "", objectSubType: "" };
}

export function isObjectSelectionComplete(
  selection: EnhancedTaggingRuleObjectSelection,
  requiresSubType: boolean,
): boolean {
  if (!selection.objectType.trim()) return false;
  if (requiresSubType && !selection.objectSubType.trim()) return false;
  return true;
}
